/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import {
  Action,
  Ad,
  AdBreak,
  AdEvent,
  AdPlaybackPolicy,
  AdPlaybackPolicyHandler,
  PLAYBACK_STATES,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackOptions,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
  PlaybackStatistics,
  Player,
  PlayerContext,
  Promo,
  PromoPlayer,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  AdPlayer,
  AdComposedPlayer,
  TrackVariantInfo,
  AdTechnology,
  AdEventListener,
} from '@quickplay/player-interface';
import {
  AnyFunc,
  createEventEmitter,
  createLogger,
  Error,
  EventEmitter,
  eventObject,
  Logger,
  LoggerLevel,
} from '@quickplay/foundation';
import { PromoPlaybackPolicyHandler } from './policy/PromoPlaybackPolicyHandler';
import {
  getSeekPositionFor,
  isAdBreakEndEvent,
  isAdBreakStartEvent,
  isAdEndEvent,
  isAdStartEvent,
  prepareAdBreak,
} from './PromoPlayerUtil';

const TAG_NAME = 'FL_PROMO_PLAYER';

/**
 * Playhead positions are given in seconds and can contain decimals, for example, 37.584s.
 * The constant TIME_THRESHOLD is defined as 1/1000 of a second as this is accurate enough to
 * perform operations with playhead positions, for example, to determine that a playhead
 * position is different from another playhead position.
 *
 * A difference between two different playhead positions (both of them given in seconds) is done
 * by comparing the difference between them, and if the absolute value of that difference is less
 * than 1/1000 second then they are considered to be equal otherwise they are considered to be
 * different.
 *
 * Examples:
 * abs(7.584156 - 7.584159) = 0.000003 is less than 0.001 so these are considered equal
 * abs(7.588 - 7.590) = 0.002 is greater than 0.001 so these are considered different
 */
export const TIME_THRESHOLD = 0.001;

export class PromoPlayerImpl implements PromoPlayer {
  private readonly logger: Logger;
  private readonly adPlaybackPolicyHandler: AdPlaybackPolicyHandler;

  private currentTime_ = 0;
  private duration_ = NaN;
  private playerContext_: PlayerContext = PlayerContext.MAIN;
  private eventEmitter: EventEmitter = createEventEmitter();
  private ads: Ad[] | undefined;
  private adBreaks: AdBreak[] | undefined;
  private currentAdBreak_: AdBreak | undefined;
  private currentAd_: Ad | undefined;
  private shouldReportAdProgress = false;
  /**
   * The last reported playhead position. When playback starts the current position is 0 so previousPlayhead
   * is set to -TIME_THRESHOLD instead of undefined.
   *
   * This is useful to determine whether there is a recent promo event such as ad break started, ad break ended, ad
   * started or ad ended. The way this is determined is by comparing the player state at previousPlayhead position
   * and the player state at current playhead position.
   *
   * @private
   */
  private previousPlayhead: number = -TIME_THRESHOLD;

  constructor(
    private internalPlayer: Player,
    private promos: Promo[],
    adPlaybackPolicy?: AdPlaybackPolicy,
    logger?: Logger,
  ) {
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);

    if (adPlaybackPolicy) {
      this.adPlaybackPolicyHandler = new PromoPlaybackPolicyHandler(internalPlayer, adPlaybackPolicy, this.logger);
    } else {
      const defaultAdPlaybackPolicy = {
        fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_ALL,
        rewindMode: PlaybackRewindMode.TRICK_RW_PLAY_ALL,
        autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_PLAY_ALL,
        repeatMode: PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY,
        interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
      };

      this.adPlaybackPolicyHandler = new PromoPlaybackPolicyHandler(
        internalPlayer,
        defaultAdPlaybackPolicy,
        this.logger,
      );
    }

    this.initialize();
  }

  private initialize = (): void => {
    const { adBreaks_, ads_ } = prepareAdBreak(this.promos, this.duration_);

    this.adBreaks = adBreaks_;
    this.ads = ads_;

    this.adPlaybackPolicyHandler.setAdBreaks(this.adBreaks ?? []);

    this.subscribeToInternalPlayerEvents();

    this.initializeWithInitialPlaybackTime();
  };

  /**
   * If initial playback time is provided and is greater than 0 this method will
   * calculate the relative initial playback time and save it back to the
   * player playback options to be used later.
   */
  private initializeWithInitialPlaybackTime = (): void => {
    if (this.isLive || !this.player.playbackOptions || !this.player.playbackOptions.initialPlaybackTime) {
      return;
    }

    // this is the initial playback position with PREROLL ads duration + player initial playback time
    let initialPlaybackTimeWithAds = getSeekPositionFor(
      this.player.playbackOptions.initialPlaybackTime,
      this.adBreaks ?? [],
    );

    initialPlaybackTimeWithAds = this.adPlaybackPolicyHandler.willSeekTo(initialPlaybackTimeWithAds);

    this.logger.log(`[PROMO] overriding initial playback time`, initialPlaybackTimeWithAds);

    this.setPlaybackOptions({
      ...this.player.playbackOptions,
      initialPlaybackTime: initialPlaybackTimeWithAds,
    });
  };

  private instanceOfAdComposedPlayer = (player: Player): player is AdComposedPlayer => {
    return 'playerContext' in player;
  };

  private isAdPlayer = (player: Player): player is AdPlayer => {
    return 'remainingTime' in player;
  };

  adEventListener?: AdEventListener;

  adSessionId?: string;

  get availableBitrates(): number[] {
    return this.player.availableBitrates;
  }

  get currentEpochTime(): number {
    return this.player.currentEpochTime;
  }

  get currentTime(): number {
    return this.adPlaybackPolicyHandler.getCurrentTime(this.currentAdBreak_, this.ads);
  }

  get duration(): number {
    return this.adPlaybackPolicyHandler.getDuration(this.currentAdBreak_, this.ads);
  }

  get internalPlayerName(): string {
    return this.player.internalPlayerName;
  }

  get internalPlayerVersion(): string {
    return this.player.internalPlayerVersion;
  }

  get sdkVersion(): string | undefined {
    return this.player.sdkVersion;
  }

  get isBuffering(): boolean {
    return this.player.isBuffering;
  }

  get isLive(): boolean {
    return this.player.isLive;
  }

  get isMute(): boolean | undefined {
    return this.player.isMute;
  }

  get volume(): number {
    return this.player.volume;
  }

  set volume(volumeLevel: number) {
    this.player.volume = volumeLevel;
  }

  get isSeeking(): boolean {
    return this.player.isSeeking;
  }

  get maxBitrate(): number | undefined {
    return this.player.maxBitrate;
  }

  set maxBitrate(maxBitrate) {
    this.player.maxBitrate = maxBitrate;
  }
  get minBitrate(): number | undefined {
    return this.player.minBitrate;
  }

  set minBitrate(minBitrate) {
    this.player.minBitrate = minBitrate;
  }

  get playbackOptions(): PlaybackOptions | undefined {
    return this.player.playbackOptions;
  }

  get playbackRate(): number {
    return this.player.playbackRate;
  }

  set playbackRate(playbackRate) {
    this.player.playbackRate = playbackRate;
  }

  get playbackStatistics(): PlaybackStatistics | undefined {
    return this.player.playbackStatistics;
  }

  get player(): Player {
    return this.internalPlayer;
  }

  get playerContext(): PlayerContext {
    if (
      this.playerContext_ !== PlayerContext.PROMO &&
      this.internalPlayer &&
      this.instanceOfAdComposedPlayer(this.internalPlayer)
    ) {
      return this.internalPlayer.playerContext;
    }

    return this.playerContext_;
  }

  get playerState(): string {
    return this.player.playerState;
  }

  get rawLibrary(): any {
    return this.player.rawLibrary;
  }

  get rawPlayer(): any {
    return this.player.rawPlayer;
  }

  get remainingTime(): number {
    // if this is playing a promo means this is a PROMO player so we
    // return the current promo remaining time based on current promo
    // duration and the player current time
    if (this.playerContext === PlayerContext.PROMO && this.currentAd_) {
      return this.currentAd_.duration - this.currentTime;
    }

    // if the underlying player is an AdPlayer (IMAPlayer, YospacePlayer, etc.)
    // then we just forward this call to the underlying player
    if (this.isAdPlayer(this.player)) {
      return this.player.remainingTime;
    }

    // this is not an AdPlayer so there's no remaining time
    return 0;
  }

  get adInsertionType(): AdTechnology | undefined {
    // if the underlying player is an AdPlayer (IMAPlayer, Yospace Player, etc.)
    // then we just forward this call to the underlying player
    if (this.isAdPlayer(this.player)) {
      return this.player.adInsertionType;
    }
    // this is not an AdPlayer so there's no ad insertion type
    return undefined;
  }

  abort(error: Error): void {
    this.player.abort(error);
  }

  accessFunctionHandler(json: string): void {
    this.player.accessFunctionHandler(json);
  }

  currentOffsetFromLiveEdge(): number {
    return this.player.currentOffsetFromLiveEdge();
  }

  discardAdBreak(): void {
    this.adPlaybackPolicyHandler.discardAdBreak(this.currentAdBreak_);
  }

  getActiveTracks(): TrackInfo[] {
    return this.player.getActiveTracks();
  }

  getVariantTracks(): TrackVariantInfo[] {
    return this.player.getVariantTracks();
  }

  getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
    return this.player.getAvailableTrackVariants(trackType);
  }

  getAllTracks(): TrackInfo[] {
    return this.player.getAllTracks();
  }

  getCuePoints(): number[] {
    return !this.adBreaks ? [] : this.adBreaks.map((adBreak: AdBreak) => adBreak.startTime);
  }

  async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
    return this.player.getThumbnail(position);
  }

  load(): void {
    this.player.load();
  }

  muteOrUnmute(): void {
    this.player.muteOrUnmute && this.player.muteOrUnmute();
  }

  pause(): void {
    this.player.pause();
  }

  play(): void {
    this.player.play();
  }

  resize(_width: number, _height: number, _isFullscreen: boolean): void {
    if (this.isAdPlayer(this.player)) {
      this.player.resize(_width, _height, _isFullscreen);
    }
  }

  seek(seekPoint: number): void {
    this.player.seek(this.adPlaybackPolicyHandler.willSeekTo(getSeekPositionFor(seekPoint, this.adBreaks ?? [])));
  }

  seekToLiveEdge(): void {
    this.player.seekToLiveEdge();
  }

  seekableRange(): SeekRangeInfo {
    if (this.player.isLive) {
      return this.player.seekableRange();
    }

    return {
      start: 0,
      end: this.duration,
    };
  }

  selectTrack(track?: TrackInfo): void {
    this.player.selectTrack(track);
  }

  send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
    this.player.send(eventName, action, metadata);
  }

  setPlaybackOptions(playbackOptions: PlaybackOptions): void {
    this.player.setPlaybackOptions(playbackOptions);
  }

  restrictQuality(enableRestriction: boolean): void {
    this.player.restrictQuality(enableRestriction);
  }

  availableMimeTypes(trackType: TrackType): Array<string> {
    return this.player.availableMimeTypes(trackType);
  }

  unloadAndSavePreload(): Promise<void> {
    return this.player.unloadAndSavePreload();
  }

  restoreFromPreload(): void {
    this.player.restoreFromPreload();
  }

  setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
    return this.player.setPreferredMimeTypes(trackType, mimeTypes);
  }

  setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
    this.player.setRequestInterceptor(requestInterceptor);
  }

  setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
    this.player.setResponseInterceptor(responseInterceptor);
  }

  skipAd(): void {
    this.adPlaybackPolicyHandler.skipAd(this.currentAd_, this.ads);
  }

  stop(): Promise<void> {
    return this.player.stop();
  }

  subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.subscribe(eventName, callback);
    this.player.subscribe(eventName, callback);
  }

  unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
    this.eventEmitter.unsubscribe(eventName, callback);
    this.player.unsubscribe(eventName, callback);
  }

  /**
   * Subscribe this Promo player to the internal player events.
   */
  private subscribeToInternalPlayerEvents = (): void => {
    this.player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.subscribe('progressupdate', this.onProgressUpdate);
  };

  /**
   * Unsubscribe this Promo player from the internal player events
   */
  private unsubscribeFromInternalPlayerEvents = (): void => {
    this.player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.unsubscribe('progressupdate', this.onProgressUpdate);
  };

  /**
   * Event listener for 'playbackstatechanged' event.
   * @param playbackState
   */
  private onPlaybackStateChanged = (playbackState: PLAYBACK_STATES): void => {
    switch (playbackState) {
      case PLAYBACK_STATES.IDLE: {
        this.unsubscribeFromInternalPlayerEvents();
        break;
      }
      case PLAYBACK_STATES.LOADED: {
        const cuePoints = this.getCuePoints();

        if (cuePoints) {
          this.eventEmitter.publish('adcuepoints', cuePoints);
        }

        break;
      }
      default: {
        // ignore
      }
    }
  };

  /**
   * Event listener for 'progressupdate' event.
   */
  private onProgressUpdate = (): void => {
    if (this.currentAd_ && this.shouldReportAdProgress) {
      const progress = this.internalPlayer.currentTime - this.currentAd_.start;
      this.eventEmitter.publish('adprogressupdate', this.currentAd_, progress);
    }

    this.currentTime_ = this.player.currentTime;
    this.duration_ = this.player.duration;

    this.evaluateAdEvents();

    this.previousPlayhead = this.currentTime_;
  };

  /**
   * Searches the player current time for ad related events.
   */
  private evaluateAdEvents = (): void => {
    this.evaluateAdBreakStartedEvent();

    this.evaluateAdEndedEvent();

    this.evaluateAdStartedEvent();

    this.evaluateAdBreakEndedEvent();
  };

  /**
   * Determines if an Ad Break start event ocurred. If an Ad Break started then this will
   * change the player's context and emit the corresponding events to notify the client.
   */
  private evaluateAdBreakStartedEvent = (): void => {
    const adBreak = isAdBreakStartEvent(this.currentTime_, this.previousPlayhead, this.adBreaks);

    if (adBreak) {
      this.logger.log('[PROMO] onAdvertBreakStart', adBreak);
      this.playerContext_ = PlayerContext.PROMO;
      this.eventEmitter.publish('playercontextchanged', this.playerContext_);
      this.currentAdBreak_ = adBreak;
      this.eventEmitter.publish(AdEvent.ADVERT_BREAK_START, this.currentAdBreak_);
    }
  };

  /**
   * Determines if an Ad start event ocurred. If an Ad started then this will
   * emit the corresponding event to notify the client.
   */
  private evaluateAdStartedEvent = (): void => {
    const ad = isAdStartEvent(this.currentTime_, this.previousPlayhead, this.adBreaks, this.ads);

    if (ad) {
      this.logger.log('[PROMO] onAdvertStart', ad);
      this.shouldReportAdProgress = true;
      this.currentAd_ = ad;
      this.eventEmitter.publish(AdEvent.ADVERT_START, this.currentAd_);
    }
  };

  /**
   * Determines if an Ad ended event ocurred. If an Ad ended then this will
   * emit the corresponding event to notify the client.
   */
  private evaluateAdEndedEvent = (): void => {
    const ad = isAdEndEvent(this.currentTime_, this.previousPlayhead, this.adBreaks, this.ads);

    if (ad) {
      this.logger.log('[PROMO] onAdvertEnd');
      this.shouldReportAdProgress = false;
      this.currentAd_ = undefined;
      this.eventEmitter.publish(AdEvent.ADVERT_END);
    }
  };

  /**
   * Determines if an Ad Break ended event ocurred. If an Ad Break ended then this will
   * change the player's context and emit the corresponding events to notify the client.
   */
  private evaluateAdBreakEndedEvent = (): void => {
    const adBreak = isAdBreakEndEvent(
      this.currentTime_,
      this.previousPlayhead,
      this.adBreaks,
      this.currentTime_,
      this.adPlaybackPolicyHandler,
    );

    if (adBreak) {
      this.logger.log('[PROMO] onAdvertBreakEnd');
      this.playerContext_ = PlayerContext.MAIN;
      this.eventEmitter.publish('playercontextchanged', this.playerContext_);
      this.currentAdBreak_ = undefined;
      this.eventEmitter.publish(AdEvent.ADVERT_BREAK_END);
    }
  };
}
